/**
 * MCOS RETAIN, one patient.
 *
 * Built to the client's Leila Haddad mockup (`docs/design-refs-2026-09-23/
 * 3-retain-patient-detail.jpeg`). His spec: "Top section: patient avatar/
 * initials, name and demographics, treatment/clinical tags, Priority Review
 * alert. Four summary cards: RETAIN Score, Risk Level, Continuation Intent,
 * Last Check-In. Main body: large 6-month RETAIN score trend chart, Key
 * Trends card, Why MCOS Flagged This Patient, In Their Own Words, MCOS
 * Recommended Action. Put technical rule IDs and scoring logic inside a
 * collapsed: View Detailed RETAIN Analysis."
 *
 * It answers his clinical hierarchy in order: who the patient is (the header),
 * the most important issue (the alert box and the four cards), what changed
 * (the trend and Key Trends), why MCOS is flagging it (the reasons and the
 * patient's own words) and what to do next (the recommended action).
 *
 * Every figure comes from one request, `/retain/patients/:id/overview`, which
 * reads what the scoring engine stored and never re-scores. Sizes and colours
 * are measured off the mockup at its native 1536 px width.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api';
import { ErrorState, overlay, useTooltipShift } from '../components/ui';
import { IconArrowLeft, IconCalendar, IconChevronDown } from '../components/icons';
import { ChangeArrow, INK, IconWarning, TIER_STYLE, pointsText, scoreText, type RiskTier } from './Retain';

/* ------------------------------------------------------------------ *
 * Data
 * ------------------------------------------------------------------ */

interface TrendPoint {
  month: number;
  value: number | string | null;
  display: string;
  level: number | null;
}

interface KeyTrend {
  key: 'continuation' | 'effectiveness' | 'sideEffects';
  label: string;
  sublabel: string;
  kind: 'numeric' | 'categorical';
  points: TrendPoint[];
}

type ReasonKey = 'safety' | 'satisfaction' | 'continuation' | 'tolerability' | 'engagement' | 'confidence' | 'health';

interface Reason {
  key: ReasonKey;
  title: string;
  detail: string;
  worsened: boolean;
}

interface AnalysisEntry {
  month: number;
  assessmentAt: string;
  retainScore: number | null;
  riskTier: RiskTier;
  trendDelta: number | null;
  modelVersion: string;
  questionVersion: string;
  recommendedAction: string;
  primaryDriver: string | null;
  secondaryDriver: string | null;
  continuationTrend: string | null;
  answers: Record<string, unknown>;
  /** Recorded with the check-in, never scored. */
  reportedWeightLb?: number | null;
  progressPhotoUrl?: string | null;
  goalOutcomes?: Record<string, unknown> | null;
  rules: { ruleId: string; output: string; because: string; action: string; priority: number }[];
  hardAlerts: { ruleId: string; alert: string; routing: string; because: string }[];
  domains: Record<string, number | null>;
  missingDependencies: string[];
}

interface Overview {
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    demographics: string[];
    phone: string | null;
    hasPhone: boolean;
    email: string | null;
    hasEmail: boolean;
    smsConsent: boolean;
    /** Which ways of reaching the patient are open, and why not when they are not. */
    contact: Record<'call' | 'text' | 'email', { available: boolean; reason: string | null }>;
    primaryGoal: string | null;
    goalHeadline: string | null;
    phenotypes: { tag: string; label: string }[];
  };
  regimen: { id: string; regimenCode: string; treatments: { id: string; name: string }[] } | null;
  tags: string[];
  latest: {
    id: string;
    assessmentMonth: number;
    assessmentAt: string;
    retainScore: number | null;
    riskTier: RiskTier;
    tierLabel: string;
    tierAdvice: string;
    tierHeadline: string;
    trendDirection: 'UP' | 'STABLE' | 'DOWN' | null;
    trendDelta: number | null;
    hardAlertActive: boolean;
    lastCheckIn: { days: number; label: string; date: string };
  } | null;
  continuationIntent: {
    latest: number | null;
    previous: number | null;
    monthsAgo: number | null;
    direction: 'down' | 'up' | 'same' | null;
    label: string | null;
  };
  trend: { month: number; retainScore: number | null; riskTier: RiskTier }[];
  keyTrends: KeyTrend[];
  reasons: Reason[];
  comment: { text: string; month: number; assessmentAt: string } | null;
  recommendation: { sentence: string; action: string } | null;
  analysis: AnalysisEntry[];
}

/* ------------------------------------------------------------------ *
 * The score colour scale
 * ------------------------------------------------------------------ */

/**
 * Colour by value, sampled from the mockup's chart points: deep green at the
 * top, olive through the Watch band, orange as it falls, red for significant
 * risk ("Green for stable/healthy. Yellow/orange for deterioration. Red only
 * for significant risk").
 */
const SCALE: [number, [number, number, number]][] = [
  [0, [224, 44, 22]],
  [30, [225, 52, 16]],
  [42, [233, 92, 12]],
  [55, [240, 138, 2]],
  [64, [203, 142, 10]],
  [71, [147, 126, 11]],
  [80, [46, 118, 74]],
  [100, [7, 99, 76]],
];

function levelColor(value: number): string {
  const v = Math.max(0, Math.min(100, value));
  for (let index = 1; index < SCALE.length; index += 1) {
    const [top, to] = SCALE[index]!;
    const [bottom, from] = SCALE[index - 1]!;
    if (v <= top) {
      const t = (v - bottom) / (top - bottom);
      const mix = from.map((channel, i) => Math.round(channel + (to[i]! - channel) * t));
      return `rgb(${mix.join(',')})`;
    }
  }
  return 'rgb(7,99,76)';
}

/* ------------------------------------------------------------------ *
 * Icons the mockup draws that the shared set does not have
 * ------------------------------------------------------------------ */

type IconProps = { size?: number; className?: string; strokeWidth?: number };

function Svg({ size = 24, className = '', strokeWidth = 1.7, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** A flag with a waving edge (Continuation Intent, Continuation Risk). */
function IconWaveFlag(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 21V4" />
      <path d="M5 4.6c2.2-1.3 4.4-1.3 6.6 0s4.6 1.3 7.4 0v8.6c-2.8 1.3-5.2 1.3-7.4 0s-4.4-1.3-6.6 0" />
    </Svg>
  );
}

/** Rising bars (Treatment Effectiveness). */
function IconRisingBars(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 20.5h2.2v-3.2" />
      <rect x="8.2" y="12.4" width="3.8" height="8.1" rx="0.8" />
      <rect x="14.6" y="4.6" width="3.8" height="15.9" rx="0.8" />
      <path d="M3.6 15.6 6 13.4" />
    </Svg>
  );
}

/** A capsule on the diagonal (Side Effects, Treatment Tolerability). */
function IconCapsule(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M10.2 20.2a5.1 5.1 0 0 1-7.2-7.2l7.8-7.8a5.1 5.1 0 0 1 7.2 7.2Z" transform="translate(1.4 -0.6)" />
      <path d="m8.4 10.2 5.4 5.4" transform="translate(1.4 -0.6)" />
    </Svg>
  );
}

/** A rising line on two axes (Treatment Satisfaction). */
function IconChartRise(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 4v16h16" />
      <path d="m7.5 15.2 3.6-4.2 3 2.6 5.2-6.2" />
      <path d="M16 7.2h3.4v3.4" />
    </Svg>
  );
}

function IconPerson(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="8" r="3.9" />
      <path d="M4.4 20.6c0-4.2 3.4-7.2 7.6-7.2s7.6 3 7.6 7.2" />
    </Svg>
  );
}

function IconChat(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 5.8A2.3 2.3 0 0 1 6.3 3.5h11.4A2.3 2.3 0 0 1 20 5.8v8.4a2.3 2.3 0 0 1-2.3 2.3H9.6L5.4 20v-3.5H6.3" />
      <path d="M8.4 10h.01M12 10h.01M15.6 10h.01" strokeWidth={2.4} />
    </Svg>
  );
}

function IconDocument(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6.6 3h7.6l4.2 4.2v12.4A1.4 1.4 0 0 1 17 21H6.6a1.4 1.4 0 0 1-1.4-1.4V4.4A1.4 1.4 0 0 1 6.6 3Z" />
      <path d="M14 3v4.4h4.4M8.7 12.2h6.6M8.7 15.8h6.6" />
    </Svg>
  );
}

function IconHandset(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6.6 3.6h2.8l1.5 4.1-2 1.3a11.3 11.3 0 0 0 6.1 6.1l1.3-2 4.1 1.5v2.8a1.9 1.9 0 0 1-2.1 1.9A16.7 16.7 0 0 1 4.7 5.7a1.9 1.9 0 0 1 1.9-2.1Z" />
    </Svg>
  );
}

function IconEnvelope(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.4" y="5.4" width="17.2" height="13.2" rx="1.8" />
      <path d="m4.2 6.6 7.8 6.2 7.8-6.2" />
    </Svg>
  );
}

/** Three linked people, the mockup's Clinical Twin glyph. */
function IconTwin(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="5.6" r="2.6" />
      <circle cx="5.4" cy="17.6" r="2.6" />
      <circle cx="18.6" cy="17.6" r="2.6" />
      <path d="M10.6 7.9 6.8 15.3M13.4 7.9l3.8 7.4M8 17.6h8" />
    </Svg>
  );
}

function IconLightbulb(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9.3 18.2h5.4M10 21.2h4" />
      <path d="M8.2 14.8a6.2 6.2 0 1 1 7.6 0c-.6.5-1 1.2-1 2v1.4H9.2v-1.4c0-.8-.4-1.5-1-2Z" />
    </Svg>
  );
}

/** The filled quotation mark in the "In Their Own Words" bubble. */
function QuoteMark({ className = '' }: { className?: string }) {
  return (
    <svg width="27" height="20" viewBox="0 0 27 20" aria-hidden="true" className={className} fill="currentColor">
      <path d="M11.4 1.2 9.9 0C4.1 2.9 0.6 7.3 0.6 12.3c0 4.3 2.4 7.1 5.7 7.1 2.9 0 5-2.1 5-4.9 0-2.7-1.9-4.6-4.5-4.6-.6 0-1.1.1-1.5.2.6-3.3 2.8-6.3 6.1-8.9Z" />
      <path d="M26 1.2 24.5 0c-5.8 2.9-9.3 7.3-9.3 12.3 0 4.3 2.4 7.1 5.7 7.1 2.9 0 5-2.1 5-4.9 0-2.7-1.9-4.6-4.5-4.6-.6 0-1.1.1-1.5.2.6-3.3 2.8-6.3 6.1-8.9Z" />
    </svg>
  );
}

const REASON_ICON: Record<ReasonKey, (props: IconProps) => ReactElement> = {
  safety: (props) => <IconWarning {...props} />,
  satisfaction: IconChartRise,
  continuation: IconWaveFlag,
  tolerability: IconCapsule,
  engagement: IconPerson,
  confidence: IconChartRise,
  health: IconPerson,
};

/* ------------------------------------------------------------------ *
 * Shared pieces
 * ------------------------------------------------------------------ */

const CARD = 'rounded-[10px] border bg-white';

/** "H3 / card titles" in the mockup's size. */
function CardTitle({ children }: { children: ReactNode }) {
  return (
    <h2
      className="font-display text-[17px] leading-[22px] tracking-[-0.005em]"
      style={{ fontWeight: 560, color: INK.heading }}
    >
      {children}
    </h2>
  );
}

/** The small uppercase data label on the four summary cards. */
function DataLabel({ children }: { children: ReactNode }) {
  return (
    <p
      className="text-[12.5px] font-semibold uppercase leading-[16px] tracking-[0.085em]"
      style={{ color: '#2c3753' }}
    >
      {children}
    </p>
  );
}

function Hint({ children }: { children: ReactNode }) {
  const tip = useTooltipShift();
  return (
    <span className="group relative inline-flex align-middle" onPointerEnter={tip.measure} onFocus={tip.measure}>
      <button
        type="button"
        aria-label="About these reasons"
        className="grid h-[17px] w-[17px] place-items-center rounded-full border text-[10.5px] font-semibold leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/40"
        style={{ borderColor: '#9aa3ad', color: '#5c6576' }}
      >
        i
      </button>
      <span
        ref={tip.ref}
        style={tip.style}
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-72 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-[8px] bg-[#14171c] px-3 py-2 text-[12px] leading-relaxed text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {children}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export function RetainPatientPage() {
  const { patientId = '' } = useParams();
  const [analysisOpen, setAnalysisOpen] = useState(false);

  const query = useQuery({
    queryKey: ['retain', 'overview', patientId],
    queryFn: () => api.get<Overview>(`/retain/patients/${patientId}/overview`),
    enabled: Boolean(patientId),
  });

  const name = query.data ? `${query.data.patient.firstName} ${query.data.patient.lastName}` : '';
  useEffect(() => {
    document.title = name ? `${name} · MCOS RETAIN` : 'MCOS RETAIN';
  }, [name]);

  if (query.isPending) {
    return (
      <div className="xl:-mx-1">
        <BackLink />
        <div className="mt-6 space-y-4" aria-busy="true" aria-label="Loading">
          <div className="h-[100px] animate-pulse rounded-[10px] bg-white/70" />
          <div className="h-[120px] animate-pulse rounded-[10px] bg-white/70" />
          <div className="h-[250px] animate-pulse rounded-[10px] bg-white/70" />
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="xl:-mx-1">
        <BackLink />
        <div className="mt-6">
          <ErrorState error={query.error} retry={() => void query.refetch()} />
        </div>
      </div>
    );
  }

  const data = query.data;

  return (
    <div className="-mt-[19px] pb-2 xl:-mx-1">
      <Header data={data} />

      {data.latest ? (
        <>
          <section
            aria-label="Summary"
            className="mt-[18px] grid grid-cols-1 gap-[13px] sm:grid-cols-2 xl:grid-cols-[minmax(0,355fr)_minmax(0,248fr)_minmax(0,313fr)_minmax(0,290fr)]"
          >
            <ScoreCard latest={data.latest} />
            <RiskCard latest={data.latest} />
            <ContinuationCard summary={data.continuationIntent} />
            <CheckInCard latest={data.latest} />
          </section>

          <section className="mt-[18px] grid gap-[15px] xl:grid-cols-[minmax(0,682fr)_minmax(0,546fr)]">
            <TrendCard trend={data.trend} latestMonth={data.latest.assessmentMonth} />
            <KeyTrendsCard trends={data.keyTrends} />
          </section>

          <section className="mt-[16px] grid gap-[13px] xl:grid-cols-[minmax(0,435fr)_minmax(0,364fr)_minmax(0,418fr)]">
            <WhyCard reasons={data.reasons} />
            <WordsCard comment={data.comment} />
            <div className="flex flex-col gap-[6px]">
              <ActionCard data={data} />
              <AnalysisToggle open={analysisOpen} onToggle={() => setAnalysisOpen((was) => !was)} />
            </div>
          </section>

          <DetailedAnalysis open={analysisOpen} entries={data.analysis} />
        </>
      ) : (
        <div className={`${CARD} mt-[19px] px-6 py-14 text-center`} style={{ borderColor: INK.border }}>
          <p className="font-display text-[18px]" style={{ fontWeight: 560, color: INK.heading }}>
            No RETAIN check-in yet
          </p>
          <p className="mx-auto mt-1.5 max-w-md text-[14.5px]" style={{ color: INK.muted }}>
            Monthly check-ins start at month 2 of the patient's cycle. Their score, trend and reasons appear here once
            the first one is scored.
          </p>
        </div>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/retain"
      className="inline-flex h-[22px] items-center gap-[9px] text-[15.5px] leading-[22px] hover:underline"
      style={{ color: '#485469' }}
    >
      <IconArrowLeft size={20} className="ml-[1px] [stroke-width:1.9]" />
      Back to RETAIN
    </Link>
  );
}

/* ------------------------------------------------------------------ *
 * Header: who the patient is, and the one thing to know
 * ------------------------------------------------------------------ */

function Header({ data }: { data: Overview }) {
  const { patient, latest } = data;
  const initials = `${patient.firstName.charAt(0)}${patient.lastName.charAt(0)}`.toUpperCase();

  return (
    <header className="flex flex-wrap items-start justify-between gap-y-5">
      <div className="min-w-0">
        <BackLink />
        <div className="mt-[13px] flex items-start">
          <span
            aria-hidden="true"
            className="grid h-16 w-16 shrink-0 place-items-center rounded-full font-display text-[24px] sm:h-[98px] sm:w-[98px] sm:text-[37px]"
            style={{ backgroundColor: '#e7f2ec', color: '#114840', fontWeight: 480 }}
          >
            {initials}
          </span>
          <div className="ml-4 min-w-0 pt-[1px] sm:ml-[22px]">
            <h1
              className="-mt-[4px] break-words font-display text-[clamp(26px,7.5vw,36px)] leading-[1.12] tracking-[-0.01em] sm:text-[36px] sm:leading-[40px]"
              style={{ fontWeight: 560, color: '#081c48' }}
            >
              {patient.firstName} {patient.lastName}
            </h1>
            {patient.demographics.length > 0 && (
              <div className="max-sm:overflow-hidden">
                <p className="mt-[1px] flex flex-wrap items-center text-[15.5px] leading-[22px] max-sm:-ml-[20px] sm:text-[18px] sm:leading-[24px]" style={{ color: '#4c5a76' }}>
                  {patient.demographics.map((part, index) => (
                    <span key={part} className="flex items-center">
                      <span
                        aria-hidden="true"
                        className={`text-[11px] max-sm:w-[20px] max-sm:text-center sm:mx-[14px] ${index === 0 ? 'sm:hidden' : ''}`}
                        style={{ color: '#56628a' }}
                      >
                        •
                      </span>
                      {part}
                    </span>
                  ))}
                </p>
              </div>
            )}
            {data.tags.length > 0 && (
              <ul className="mt-[11px] flex flex-wrap gap-2 sm:gap-[14px]">
                {data.tags.map((tag) => (
                  <li
                    key={tag}
                    className="flex h-[25px] items-center rounded-[6px] px-[11px] text-[13px]"
                    style={{ backgroundColor: '#ebf3fe', color: '#4e5d7d' }}
                  >
                    {tag}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col items-stretch sm:items-end lg:w-auto">
        <HeaderActions patientId={patient.id} />
        {latest && <TierBox latest={latest} />}
      </div>
    </header>
  );
}

function HeaderActions({ patientId }: { patientId: string }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 4000);
    return () => window.clearTimeout(timer);
  }, [saved]);

  const secondary =
    'inline-flex h-[42px] items-center justify-center gap-[12px] rounded-[8px] border bg-white text-[15px] font-medium transition-colors hover:bg-mint';

  return (
    <div className="relative mt-[10px] flex flex-wrap justify-start gap-2 sm:justify-end sm:gap-[15px]">
      <Link
        to={`/messages?patient=${patientId}`}
        className={`${secondary} px-4 sm:w-[173px] sm:px-0`}
        style={{ borderColor: '#c3d7d4', color: '#1f4a43' }}
      >
        <IconChat size={22} className="text-forest" strokeWidth={1.8} />
        Message Patient
      </Link>
      <Link
        to={`/patients/${patientId}`}
        className={`${secondary} px-4 sm:w-[234px] sm:px-0`}
        style={{ borderColor: '#c3d7d4', color: '#1f4a43' }}
      >
        <IconPerson size={21} className="text-forest" strokeWidth={1.8} />
        View Full Patient Profile
      </Link>
      <button
        type="button"
        onClick={() => setNoteOpen(true)}
        className="inline-flex h-[40px] items-center justify-center gap-[12px] rounded-[8px] bg-forest px-4 sm:w-[146px] sm:px-0 text-[15px] font-medium text-white transition-colors hover:bg-forest-deep"
      >
        <IconDocument size={20} strokeWidth={1.8} />
        Add Note
      </button>
      {saved && (
        <p
          role="status"
          className="absolute right-0 top-full z-10 mt-2 rounded-[8px] bg-forest px-3 py-1.5 text-[13px] text-white shadow"
        >
          Note saved to the patient record
        </p>
      )}
      {noteOpen && (
        <AddNoteDialog
          patientId={patientId}
          onClose={() => setNoteOpen(false)}
          onSaved={() => {
            setNoteOpen(false);
            setSaved(true);
          }}
        />
      )}
    </div>
  );
}

/**
 * The light box under the buttons. Red and "Priority Review" for a Priority
 * review, as drawn; any other tier gets the same box in its own colours, so
 * the box never says something the Risk Level card does not.
 */
function TierBox({ latest }: { latest: NonNullable<Overview['latest']> }) {
  const style = TIER_STYLE[latest.riskTier];
  const priority = latest.riskTier === 'PRIORITY_REVIEW';
  return (
    <div
      role={priority ? 'alert' : 'status'}
      className="mt-[19px] flex min-h-[72px] w-[396px] max-w-full items-center rounded-[10px] border py-2 pl-4 pr-4 sm:h-[72px] sm:py-0 sm:pl-[27px]"
      style={{
        backgroundColor: priority ? '#fdeaec' : style.fill,
        borderColor: priority ? '#f8dde0' : 'transparent',
      }}
    >
      <span className="shrink-0" style={{ color: priority ? '#d0142a' : style.text }}>
        <IconWarning size={48} strokeWidth={1.55} />
      </span>
      <div className="ml-[11px] min-w-0">
        <p
          className="font-display text-[20.5px] leading-[24px]"
          style={{ fontWeight: 560, color: priority ? '#d4111b' : style.text }}
        >
          {style.label}
        </p>
        <p title={latest.tierHeadline} className="mt-[3px] text-[15.5px] leading-[20px] sm:truncate" style={{ color: '#26213d' }}>
          {latest.tierHeadline}
        </p>
      </div>
    </div>
  );
}

function AddNoteDialog({
  patientId,
  onClose,
  onSaved,
}: {
  patientId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [body, setBody] = useState('');
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: () => api.post(`/patients/${patientId}/notes`, { body: body.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['patient-notes', patientId] });
      onSaved();
    },
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const length = body.trim().length;

  return overlay(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#14171c]/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-note-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        className="w-full max-w-[480px] rounded-[12px] border bg-white p-6 text-left shadow-[0_16px_48px_rgba(20,23,28,0.25)]"
        style={{ borderColor: INK.border }}
        onSubmit={(event) => {
          event.preventDefault();
          if (length > 0 && length <= 4000) save.mutate();
        }}
      >
        <h2 id="add-note-title" className="font-display text-[20px]" style={{ fontWeight: 560, color: INK.heading }}>
          Add a note
        </h2>
        <p className="mt-1 text-[13.5px]" style={{ color: INK.muted }}>
          Saved to the patient record with your name and the time. Notes cannot be edited afterwards.
        </p>
        <textarea
          autoFocus
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={5}
          maxLength={4000}
          placeholder="What was discussed, or what should happen next"
          className="mt-4 w-full resize-y rounded-[8px] border bg-white px-3 py-2.5 text-[14.5px] leading-[21px] focus:border-[#9fbcb5] focus:outline-none"
          style={{ borderColor: '#d9dfe3', color: INK.body }}
        />
        <p className="mt-1 text-right text-[12px]" style={{ color: INK.faint }}>
          {length} / 4000
        </p>
        {save.isError && (
          <p className="mt-2 text-[13.5px] text-alert">
            {save.error instanceof Error ? save.error.message : 'The note could not be saved.'}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-[40px] rounded-[8px] border bg-white px-5 text-[15px] font-medium hover:bg-mint"
            style={{ borderColor: '#c3d7d4', color: '#1f4a43' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={length === 0 || save.isPending}
            className="h-[40px] rounded-[8px] bg-forest px-5 text-[15px] font-medium text-white hover:bg-forest-deep disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Saving' : 'Save note'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The four summary cards
 * ------------------------------------------------------------------ */

type Latest = NonNullable<Overview['latest']>;

function SummaryCard({ children }: { children: ReactNode }) {
  return (
    <div className={`${CARD} relative h-[120px] min-w-0 pl-[25px] pr-3 pt-[10px]`} style={{ borderColor: INK.border }}>
      {children}
    </div>
  );
}

function ScoreCard({ latest }: { latest: Latest }) {
  const style = TIER_STYLE[latest.riskTier];
  const delta = latest.trendDelta;
  return (
    <SummaryCard>
      <DataLabel>RETAIN Score</DataLabel>
      <div className="mt-[3px] flex items-start">
        <p className="flex shrink-0 items-baseline whitespace-nowrap">
          <span
            className="-ml-[3px] font-display text-[62px] leading-[70px]"
            style={{ fontWeight: 560, color: style.score }}
          >
            {scoreText(latest.retainScore)}
          </span>
          <span className="relative -top-[2px] ml-[9px] font-display text-[25px]" style={{ fontWeight: 340, color: '#1f2d4e' }}>
            / 100
          </span>
        </p>
        <div className="absolute left-[178px] top-[50px] flex min-w-0 items-start">
          {delta === null ? (
            <p className="text-[14px]" style={{ color: INK.faint }}>
              First check-in
            </p>
          ) : delta === 0 ? (
            <p className="text-[14px]" style={{ color: INK.faint }}>
              No change from last month
            </p>
          ) : (
            <>
              <span className="mt-[4px] shrink-0" style={{ color: delta < 0 ? '#d4111b' : '#0b5345' }}>
                <ChangeArrow down={delta < 0} width={11} height={15} />
              </span>
              <div className="ml-[14px] min-w-0">
                <p
                  className="whitespace-nowrap text-[16.5px] font-semibold leading-[20px]"
                  style={{ color: delta < 0 ? '#c9262e' : '#0b5345' }}
                >
                  {pointsText(delta, 'points')}
                </p>
                <p className="mt-[3px] whitespace-nowrap text-[14px] leading-[18px]" style={{ color: '#5f6983' }}>
                  from last month
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </SummaryCard>
  );
}

function RiskCard({ latest }: { latest: Latest }) {
  const style = TIER_STYLE[latest.riskTier];
  return (
    <SummaryCard>
      <DataLabel>Risk Level</DataLabel>
      <span
        className="-ml-[3px] mt-[9px] inline-flex h-[38px] min-w-[148px] items-center rounded-[8px] pl-[8px] pr-[18px] text-[17px] font-semibold"
        style={{ backgroundColor: style.fill === '#fde7ea' ? '#fee6e6' : style.fill, color: style.text }}
      >
        <IconWarning size={32} strokeWidth={1.6} className="shrink-0" />
        <span className="ml-[22px] whitespace-nowrap">{style.label}</span>
      </span>
      <p className="ml-[51px] mt-0 max-w-[112px] text-[13.5px] leading-[17px]" style={{ color: '#5f6983' }}>
        {latest.tierAdvice}
      </p>
    </SummaryCard>
  );
}

function ContinuationCard({ summary }: { summary: Overview['continuationIntent'] }) {
  return (
    <SummaryCard>
      <DataLabel>Continuation Intent</DataLabel>
      {summary.latest === null ? (
        <p className="mt-4 text-[14.5px]" style={{ color: INK.muted }}>
          Not answered at the last check-in
        </p>
      ) : (
        <>
          <p className="mt-[5px] flex items-baseline">
            <span className="font-display text-[41px] leading-[46px]" style={{ fontWeight: 560, color: '#0b1e4c' }}>
              {summary.latest}
            </span>
            <span className="ml-[4px] font-display text-[19px]" style={{ fontWeight: 340, color: '#34405a' }}>
              / 10
            </span>
          </p>
          {summary.label ? (
            <p
              className="mt-[7px] flex items-center text-[15.5px] leading-[20px]"
              style={{
                color:
                  summary.direction === 'down' ? '#c9262e' : summary.direction === 'up' ? '#0b5345' : INK.muted,
              }}
            >
              {summary.direction !== 'same' && (
                <span className="ml-[1px] mr-[14px] shrink-0">
                  <ChangeArrow down={summary.direction === 'down'} width={11} height={15} />
                </span>
              )}
              {summary.label}
            </p>
          ) : (
            <p className="mt-[5px] text-[14px]" style={{ color: INK.faint }}>
              First answer on record
            </p>
          )}
        </>
      )}
    </SummaryCard>
  );
}

function CheckInCard({ latest }: { latest: Latest }) {
  return (
    <SummaryCard>
      <DataLabel>Last Check-In</DataLabel>
      <div className="mt-[13px] flex items-start">
        <IconCalendar size={40} className="ml-[-3px] shrink-0 text-[#0a1741] [stroke-width:1.55]" />
        <div className="ml-[15px] pt-[11px]">
          <p className="text-[17.5px] font-semibold leading-[22px]" style={{ color: '#12183e' }}>
            {latest.lastCheckIn.label}
          </p>
          <p className="mt-[4px] text-[15px] leading-[20px]" style={{ color: '#5f6983' }}>
            {latest.lastCheckIn.date}
          </p>
        </div>
      </div>
    </SummaryCard>
  );
}

/* ------------------------------------------------------------------ *
 * RETAIN Score Trend
 * ------------------------------------------------------------------ */

const CHART_HEIGHT = 190;
const CHART_MARGIN = { top: 16, right: 23, bottom: 0, left: 0 };
const X_AXIS_HEIGHT = 31;
/** The plot's pixel rows for 100 and 0, so the line's colour is keyed to the value, not the data's range. */
const PLOT_TOP = CHART_MARGIN.top;
const PLOT_BOTTOM = CHART_HEIGHT - CHART_MARGIN.bottom - X_AXIS_HEIGHT;

function TrendCard({ trend, latestMonth }: { trend: Overview['trend']; latestMonth: number }) {
  const [span, setSpan] = useState<3 | 6>(6);

  // The cycle's months, M1 to M6. RETAIN check-ins start at month 2, so M1
  // has no point; it stays on the axis because the cycle does.
  const months = useMemo(() => {
    const last = Math.max(6, latestMonth);
    const first = span === 6 ? last - 5 : Math.max(1, latestMonth - 2);
    const lastShown = span === 6 ? last : Math.max(first + 2, latestMonth);
    return Array.from({ length: lastShown - first + 1 }, (_, index) => first + index);
  }, [span, latestMonth]);

  const points = trend
    .filter((row) => row.retainScore !== null && row.month >= months[0]! && row.month <= months[months.length - 1]!)
    .map((row) => ({ month: row.month, score: Math.round(row.retainScore! * 10) / 10 }));

  const step = 1;
  const domain: [number, number] = [months[0]! - 0.234 * step, months[months.length - 1]! + 0.595 * step];
  const lineStops = [100, 80, 71, 64, 55, 42, 30, 0];

  return (
    <div className={`${CARD} relative h-[280px] min-w-0 sm:h-[249px]`} style={{ borderColor: INK.border }}>
      <div className="absolute left-[22px] top-[14px]">
        <CardTitle>RETAIN Score Trend</CardTitle>
      </div>
      <PeriodSelect value={span} onChange={setSpan} />

      {points.length === 0 ? (
        <p className="absolute inset-x-0 top-[110px] text-center text-[14.5px]" style={{ color: INK.muted }}>
          No scored check-in in this period
        </p>
      ) : (
        <div className="absolute inset-x-0 top-[80px] sm:top-[49px]" style={{ height: CHART_HEIGHT }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={CHART_MARGIN}>
              <defs>
                <linearGradient id="retainLine" gradientUnits="userSpaceOnUse" x1="0" y1={PLOT_TOP} x2="0" y2={PLOT_BOTTOM}>
                  {lineStops.map((value) => (
                    <stop key={value} offset={`${100 - value}%`} stopColor={levelColor(value)} />
                  ))}
                </linearGradient>
                <linearGradient id="retainAreaColour" x1="0" y1="0" x2="1" y2="0">
                  {points.map((point, index) => (
                    <stop
                      key={point.month}
                      offset={points.length > 1 ? `${(index / (points.length - 1)) * 100}%` : '0%'}
                      stopColor={levelColor(point.score)}
                    />
                  ))}
                </linearGradient>
                <linearGradient id="retainAreaFade" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fff" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#fff" stopOpacity={0.05} />
                </linearGradient>
                <mask id="retainAreaMask" maskContentUnits="objectBoundingBox">
                  <rect x="0" y="0" width="1" height="1" fill="url(#retainAreaFade)" />
                </mask>
              </defs>
              <CartesianGrid stroke="#eef0f2" strokeWidth={1} />
              <XAxis
                type="number"
                dataKey="month"
                domain={domain}
                ticks={months}
                allowDataOverflow
                tickFormatter={(month: number) => `M${month}`}
                tickLine={false}
                tickSize={0}
                tickMargin={10}
                height={X_AXIS_HEIGHT}
                axisLine={{ stroke: '#d9d9d9' }}
                tick={{ fontSize: 13, fill: '#6c7581' }}
              />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                width={60}
                tickLine={false}
                tickSize={0}
                tickMargin={12}
                axisLine={{ stroke: '#e3e5e8' }}
                tick={{ fontSize: 13.5, fill: '#5c6371' }}
              />
              <Area
                type="monotone"
                dataKey="score"
                stroke="none"
                fill="url(#retainAreaColour)"
                mask="url(#retainAreaMask)"
                isAnimationActive={false}
                baseValue={0}
                activeDot={false}
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke="url(#retainLine)"
                strokeWidth={2}
                isAnimationActive={false}
                activeDot={false}
                dot={(props: { cx?: number; cy?: number; index?: number; payload?: { score: number } }) => (
                  <circle
                    key={props.index}
                    cx={props.cx}
                    cy={props.cy}
                    r={5.5}
                    fill={levelColor(props.payload?.score ?? 0)}
                  />
                )}
                label={(props: { x?: number; y?: number; index?: number; value?: number }) => (
                  <text
                    key={props.index}
                    x={props.x}
                    y={(props.y ?? 0) - 15}
                    textAnchor="middle"
                    fontSize={15}
                    fill="#1b212a"
                  >
                    {Math.round(props.value ?? 0)}
                  </text>
                )}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function PeriodSelect({ value, onChange }: { value: 3 | 6; onChange: (next: 3 | 6) => void }) {
  return (
    <label className="absolute left-[22px] top-[44px] sm:left-auto sm:right-[15px] sm:top-[10px]">
      <span className="sr-only">Period</span>
      <select
        value={value}
        onChange={(event) => onChange(Number(event.target.value) as 3 | 6)}
        className="h-[31px] w-[154px] cursor-pointer appearance-none rounded-[6px] border bg-white pl-[14px] pr-8 text-[13.5px] focus:border-[#9fbcb5] focus:outline-none"
        style={{ borderColor: '#dde2e6', color: '#565e6b' }}
      >
        <option value={6}>Last 6 Months</option>
        <option value={3}>Last 3 Months</option>
      </select>
      <IconChevronDown
        size={16}
        className="pointer-events-none absolute right-[10px] top-1/2 -translate-y-1/2 text-[#3a4250] [stroke-width:2.2]"
      />
    </label>
  );
}

/* ------------------------------------------------------------------ *
 * Key Trends
 * ------------------------------------------------------------------ */

const TREND_ICON: Record<KeyTrend['key'], (props: IconProps) => ReactElement> = {
  continuation: IconWaveFlag,
  effectiveness: IconRisingBars,
  sideEffects: IconCapsule,
};

function KeyTrendsCard({ trends }: { trends: KeyTrend[] }) {
  return (
    <div className={`${CARD} relative min-w-0 pb-2 sm:h-[249px] sm:pb-0`} style={{ borderColor: INK.border }}>
      <div className="px-[27px] pt-[13px]">
        <CardTitle>Key Trends</CardTitle>
      </div>
      <ul className="mx-[17px] mt-[9px] border-t" style={{ borderColor: INK.divider }}>
        {trends.map((trend, index) => (
          <li
            key={trend.key}
            className={`flex flex-wrap items-center py-3 sm:h-[65px] sm:flex-nowrap sm:py-0 ${index < trends.length - 1 ? 'border-b' : ''}`}
            style={{ borderColor: INK.divider }}
          >
            <TrendRow trend={trend} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function TrendRow({ trend }: { trend: KeyTrend }) {
  const Icon = TREND_ICON[trend.key];
  const answered = trend.points.filter((point) => point.level !== null);
  return (
    <>
      <span
        aria-hidden="true"
        className="grid h-[48px] w-[48px] shrink-0 place-items-center rounded-full"
        style={{ backgroundColor: '#eff7f2', color: '#11584a' }}
      >
        <Icon size={27} strokeWidth={1.6} />
      </span>
      {/* On a phone the label keeps its line and the strip drops beneath it. */}
      <div className="ml-[18px] min-w-0 flex-1 max-sm:basis-[calc(100%-66px)]">
        <p title={trend.label} className="break-words text-[15px] font-semibold leading-[20px]" style={{ color: '#131d40' }}>
          {trend.label}
        </p>
        <p title={trend.sublabel} className="break-words mt-0 text-[14px] leading-[19px]" style={{ color: '#687089' }}>
          {trend.sublabel}
        </p>
      </div>
      <div className="w-[190px] shrink-0 max-sm:ml-[66px] max-sm:mt-2 sm:mr-[26px]">
        {answered.length === 0 ? (
          <p className="text-[13.5px]" style={{ color: INK.faint }}>
            Not answered yet
          </p>
        ) : (
          <>
            <Sparkline points={trend.points} />
            <Sequence trend={trend} />
          </>
        )}
      </div>
    </>
  );
}

/** Dots coloured by value on a thin line, the mockup's small trend strip. */
function Sparkline({ points }: { points: TrendPoint[] }) {
  const width = 190;
  const height = 24;
  const pad = 4;
  const n = points.length;
  const x = (index: number) => (n === 1 ? width / 2 : pad + (index * (width - 2 * pad)) / (n - 1));
  // Scaled to this series, as the mockup draws it, but never to less than 40
  // points of range, so a one-point wobble does not read as a collapse.
  const levels = points.map((point) => point.level).filter((level): level is number => level !== null);
  const middle = levels.length ? (Math.max(...levels) + Math.min(...levels)) / 2 : 50;
  const range = Math.max(40, levels.length ? Math.max(...levels) - Math.min(...levels) : 40);
  const top = Math.min(100, middle + range / 2);
  const y = (level: number) => pad + ((top - level) / range) * (height - 2 * pad);
  const placed = points
    .map((point, index) => (point.level === null ? null : { x: x(index), y: y(point.level), level: point.level }))
    .filter((point): point is { x: number; y: number; level: number } => point !== null);
  const id = useMemo(() => `spark-${Math.random().toString(36).slice(2, 9)}`, []);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" className="block">
      {placed.length > 1 && (
        <>
          <defs>
            <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={placed[0]!.x} y1="0" x2={placed[placed.length - 1]!.x} y2="0">
              {placed.map((point) => (
                <stop
                  key={point.x}
                  offset={`${((point.x - placed[0]!.x) / (placed[placed.length - 1]!.x - placed[0]!.x)) * 100}%`}
                  stopColor={levelColor(point.level)}
                />
              ))}
            </linearGradient>
          </defs>
          <polyline
            points={placed.map((point) => `${point.x},${point.y}`).join(' ')}
            fill="none"
            stroke={`url(#${id})`}
            strokeWidth={1.6}
            strokeLinejoin="round"
          />
        </>
      )}
      {placed.map((point) => (
        <circle key={point.x} cx={point.x} cy={point.y} r={3.6} fill={levelColor(point.level)} />
      ))}
    </svg>
  );
}

/** "7 → 7 → 4 → 3 → 2", or the words spread under their dots. */
function Sequence({ trend }: { trend: KeyTrend }) {
  const words = trend.kind === 'categorical';
  return (
    <p
      className={`-mt-[3px] flex items-center whitespace-nowrap text-[15px] leading-[20px] ${
        words ? 'justify-between' : 'gap-[10px]'
      }`}
      style={{ color: '#2b3446' }}
    >
      {trend.points.map((point, index) => (
        <span key={point.month} className={`flex items-center ${words ? '' : 'gap-[10px]'}`}>
          {index > 0 && (
            <span aria-hidden="true" className={words ? 'mr-[14px] text-[14px]' : 'text-[14px]'}>
              →
            </span>
          )}
          <span className={index === trend.points.length - 1 && words ? 'text-[15.5px]' : ''}>{point.display}</span>
        </span>
      ))}
    </p>
  );
}

/* ------------------------------------------------------------------ *
 * Why MCOS Flagged This Patient
 * ------------------------------------------------------------------ */

function WhyCard({ reasons }: { reasons: Reason[] }) {
  return (
    <div className={`${CARD} min-h-[336px] min-w-0 px-[20px] pb-[18px] pt-[11px]`} style={{ borderColor: INK.border }}>
      <div className="flex items-center gap-[10px] pl-[1px]">
        <CardTitle>Why MCOS Flagged This Patient</CardTitle>
        <Hint>
          Built from this month's check-in: the answers that scored lowest, the patterns the RETAIN rules found across
          the months, and any alert that overrides the score. The rule numbers are in the detailed analysis below.
        </Hint>
      </div>
      {reasons.length === 0 ? (
        <div className="mt-6 rounded-[8px] border px-5 py-8 text-center" style={{ borderColor: INK.divider }}>
          <p className="text-[15px] font-semibold" style={{ color: INK.strong }}>
            Nothing flagged this month
          </p>
          <p className="mt-1 text-[13.5px]" style={{ color: INK.muted }}>
            No answer scored under the watch line and no RETAIN pattern fired.
          </p>
        </div>
      ) : (
        <ol className="mt-[7px] space-y-[6px]">
          {reasons.map((reason, index) => {
            const Icon = REASON_ICON[reason.key];
            return (
              <li
                key={reason.key}
                className="flex min-h-[63px] items-center rounded-[9px] border py-[6px] pr-3"
                style={{ borderColor: '#eef0f1' }}
              >
                <span
                  aria-hidden="true"
                  className="ml-[1px] grid h-[44px] w-[48px] shrink-0 place-items-center rounded-[13px]"
                  style={{ backgroundColor: '#fde0e4', color: '#d11a2a' }}
                >
                  <Icon size={27} strokeWidth={1.7} />
                </span>
                <div className="ml-[20px] min-w-0">
                  <p className="flex items-center text-[15px] font-semibold leading-[20px]" style={{ color: '#1c2946' }}>
                    {index + 1}. {reason.title}
                    {reason.worsened && (
                      <span className="ml-[8px] text-[#d4111b]" aria-label="worse than before">
                        <ChangeArrow down width={9} height={12} />
                      </span>
                    )}
                  </p>
                  <p className="mt-[1px] max-w-[300px] text-[13.5px] leading-[17px]" style={{ color: '#5d6472' }}>
                    {/* A score sequence such as 7 -> 4 -> 3 -> 2 wraps as one piece, as drawn. */}
                    {reason.detail.replace(/ → /g, '\u00a0→\u00a0')}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * In Their Own Words
 * ------------------------------------------------------------------ */

/**
 * The patient's comment, verbatim. The scoring key keeps it unscored and asks
 * that the original be preserved and shown, so it is printed exactly as
 * written, in their words rather than a summary.
 */
function WordsCard({ comment }: { comment: Overview['comment'] }) {
  return (
    <div className={`${CARD} relative min-h-[336px] min-w-0`} style={{ borderColor: INK.border }}>
      <div className="px-[19px] pt-[13px]">
        <CardTitle>In Their Own Words</CardTitle>
      </div>
      <div className="relative mx-[19px] mt-[26px] min-h-[183px] rounded-[12px] border pb-6 pl-[44px] pr-[8px] pt-[37px]" style={{ borderColor: '#eaedef' }}>
        <span
          aria-hidden="true"
          className="absolute -top-[20px] left-[2px] grid h-[44px] w-[44px] place-items-center rounded-full"
          style={{ backgroundColor: '#e9f4f3', color: '#178277' }}
        >
          <QuoteMark />
        </span>
        {comment ? (
          <>
            <blockquote
              className="max-w-[270px] font-display text-[24px] italic leading-[30.5px]"
              style={{ fontWeight: 420, color: '#1f2848' }}
            >
              “{comment.text}”
            </blockquote>
            <p className="mt-[20px] text-[14.5px] leading-[20px]" style={{ color: '#6c7385' }}>
              From recent check-in
              <span className="sr-only">, month {comment.month}</span>
            </p>
          </>
        ) : (
          <>
            <p className="font-display text-[19px] italic leading-[26px]" style={{ fontWeight: 420, color: INK.muted }}>
              No comment written yet.
            </p>
            <p className="mt-3 text-[14px] leading-[20px]" style={{ color: INK.faint }}>
              The monthly check-in has an optional free-text box. Nothing has been written in it so far.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Next step (WT-07: MCOS never recommends; the card names the RETAIN key's action)
 * ------------------------------------------------------------------ */

function ActionCard({ data }: { data: Overview }) {
  const { patient, recommendation } = data;
  const button =
    'flex h-[36px] w-full items-center justify-center gap-[11px] rounded-[6px] text-[15px] transition-colors';
  return (
    <div className={`${CARD} min-w-0 px-[20px] pb-[12px] pt-[13px]`} style={{ borderColor: INK.border }}>
      <div className="pl-[2px]">
        <CardTitle>Next step</CardTitle>
      </div>
      <div className="mt-[8px] flex min-h-[94px] items-start rounded-[10px] pb-[6px] pl-[9px] pr-[10px] pt-[8px]" style={{ backgroundColor: '#eaf4ef' }}>
        <span
          aria-hidden="true"
          className="grid h-[48px] w-[48px] shrink-0 place-items-center rounded-full"
          style={{ backgroundColor: '#daeede', color: '#16493a' }}
        >
          <IconLightbulb size={28} strokeWidth={1.6} />
        </span>
        <p className="ml-[12px] pt-[1px] text-[14.5px] leading-[20px]" style={{ color: '#1b3634' }}>
          {recommendation?.sentence ?? 'No next step until the first check-in is scored.'}
        </p>
      </div>
      <div className="mt-[3px] space-y-[7px]">
        {/* Brian, 26 Sept: "should offer multiple ways to contact the patient
            (e.g. text, call, email), not a single option." */}
        <div role="group" aria-label="Contact patient" className="grid grid-cols-3 gap-[7px]">
          <ContactButton
            label="Call"
            icon={<IconHandset size={19} strokeWidth={1.7} />}
            channel={patient.contact.call}
            href={patient.phone ? `tel:${patient.phone.replace(/[^\d+]/g, '')}` : null}
          />
          <ContactButton
            label="Text"
            icon={<IconChat size={19} strokeWidth={1.7} />}
            channel={patient.contact.text}
            to={`/messages?patient=${patient.id}`}
          />
          <ContactButton
            label="Email"
            icon={<IconEnvelope size={19} strokeWidth={1.7} />}
            channel={patient.contact.email}
            href={patient.email ? `mailto:${patient.email}` : null}
          />
        </div>
        <Link
          to={`/patients/${patient.id}/workspace?tab=plan`}
          className={`${button} border bg-white hover:bg-mint`}
          style={{ borderColor: '#b9cfca', color: '#1d4a40' }}
        >
          <IconDocument size={19} strokeWidth={1.7} className="text-forest" />
          Review Treatment Plan
        </Link>
        <Link
          to={`/patients/${patient.id}/workspace?tab=twin`}
          className={`${button} border bg-white hover:bg-mint`}
          style={{ borderColor: '#b9cfca', color: '#1d4a40' }}
        >
          <IconTwin size={22} strokeWidth={1.7} className="text-forest" />
          View Clinical Twin
        </Link>
      </div>
    </div>
  );
}

/**
 * One way of reaching the patient. When it is not possible the button stays
 * in place, greyed, and says why on hover and to a screen reader, so the
 * clinician learns what is missing rather than wondering where it went.
 */
function ContactButton({
  label,
  icon,
  channel,
  href,
  to,
}: {
  label: string;
  icon: ReactNode;
  channel: { available: boolean; reason: string | null };
  href?: string | null;
  to?: string;
}) {
  const base =
    'flex h-[36px] min-w-0 items-center justify-center gap-[8px] rounded-[6px] bg-forest px-2 text-[15px] font-medium text-white transition-colors';
  if (!channel.available || (!href && !to)) {
    const reason = channel.reason ?? 'Not available';
    return (
      <span role="link" aria-disabled="true" title={reason} className={`${base} cursor-not-allowed opacity-45`}>
        {icon}
        <span className="truncate">{label}</span>
        <span className="sr-only">, {reason}</span>
      </span>
    );
  }
  const className = `${base} hover:bg-forest-deep`;
  return to ? (
    <Link to={to} className={className}>
      {icon}
      <span className="truncate">{label}</span>
    </Link>
  ) : (
    <a href={href!} className={className}>
      {icon}
      <span className="truncate">{label}</span>
    </a>
  );
}

/* ------------------------------------------------------------------ *
 * View Detailed RETAIN Analysis
 *
 * Everything below is the scoring logic the rest of the screen leaves out:
 * rule IDs, the seven domain scores, the hard alerts, the answers each month
 * and the model version. Collapsed until a clinician asks for it.
 * ------------------------------------------------------------------ */

function AnalysisToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls="retain-detailed-analysis"
      className={`${CARD} flex min-h-[59px] w-full items-center pl-[22px] pr-[23px] text-left transition-colors hover:bg-[#fbfcfb]`}
      style={{ borderColor: INK.border }}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-medium leading-[20px]" style={{ color: '#23304d' }}>
          View Detailed RETAIN Analysis
        </span>
        <span className="mt-[1px] block text-[14px] leading-[19px]" style={{ color: '#6d768c' }}>
          See all triggered rules and scoring details
        </span>
      </span>
      <IconChevronDown
        size={20}
        className={`shrink-0 text-[#23304d] transition-transform duration-200 ease-out [stroke-width:2] ${open ? 'rotate-180' : ''}`}
      />
    </button>
  );
}

const DOMAIN_LABEL: [string, string][] = [
  ['continuationIntent', 'Continuation intent'],
  ['treatmentSatisfaction', 'Treatment satisfaction'],
  ['confidence', 'Goal confidence'],
  ['treatmentBurden', 'Treatment burden'],
  ['tolerability', 'Tolerability'],
  ['healthTrend', 'Energy and well-being'],
  ['stopIntent', 'Stop or change intent'],
];

/** The questions behind the answers, by the Typeform reference the engine reads. */
const QUESTIONS: [string, string][] = [
  ['retain_progress_satisfaction', 'Satisfaction with progress this month (0 to 10)'],
  ['retain_treatment_effectiveness', 'How effective the plan has been (0 to 10)'],
  ['retain_goal_confidence', 'Confidence in reaching their goals (0 to 10)'],
  ['retain_energy', 'Overall energy (0 to 10)'],
  ['retain_wellbeing', 'Overall well-being (0 to 10)'],
  ['retain_side_effects', 'Side effects or problems with treatment'],
  ['retain_treatment_difficulty', 'How difficult the plan has been to follow'],
  ['retain_missed_treatment', 'Missed or delayed doses this month'],
  ['retain_stop_change_intent', 'Considering stopping or changing treatment'],
  ['retain_continuation_intent', 'Likely to continue next month (0 to 10)'],
  ['retain_current_safety_symptoms', 'Current safety symptoms (months 3 and 6, never scored)'],
];

function answerText(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not answered';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not answered';
  return String(value);
}

function DetailedAnalysis({ open, entries }: { open: boolean; entries: AnalysisEntry[] }) {
  return (
    <div
      id="retain-detailed-analysis"
      className={`grid transition-all duration-300 ease-out ${open ? 'mt-[13px] grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      aria-hidden={!open}
    >
      <div className="overflow-hidden">
        <div className={`${CARD} px-6 py-5`} style={{ borderColor: INK.border }}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <CardTitle>Detailed RETAIN analysis</CardTitle>
            <p className="text-[13px]" style={{ color: INK.faint }}>
              Deterministic: the same answers always give the same score, tier and rules.
            </p>
          </div>
          <div className="mt-4 space-y-6">
            {entries.map((entry) => (
              <AnalysisMonth key={entry.month} entry={entry} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The muscle module scales repeated each month, as a clinician reads them. */
const OUTCOME_LABEL: Record<string, string> = {
  strength: 'Strength',
  bodyCompositionSatisfaction: 'Body composition satisfaction',
  recovery: 'Recovery',
  recoveryProblems: 'Recovery problems',
  trainingLimitedByInjury: 'Pain or injury limits training',
  trainingDaysBand: 'Strength training',
  soreness: 'Muscle soreness lasting over two days',
  exerciseTypes: 'Exercise types',
  injuryArea: 'Area limiting training',
};

function outcomeText(key: string, value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'number' && key !== 'trainingDaysBand') return `${value} of 10`;
  return String(value);
}

/**
 * What the patient gave with this check-in that the score does not use: the
 * weight at the top of the form, the month 3 and 6 progress photo, and the
 * muscle and recovery scales.
 */
function RecordedWithCheckIn({ entry }: { entry: AnalysisEntry }) {
  const outcomes = Object.entries(entry.goalOutcomes ?? {});
  if (entry.reportedWeightLb == null && !entry.progressPhotoUrl && outcomes.length === 0) return null;
  return (
    <div className="mt-3 rounded-[8px] px-3 py-2.5 text-[13.5px]" style={{ backgroundColor: INK.divider }} data-testid="recorded-with-checkin">
      <p className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: INK.muted }}>
        Recorded with this check-in, not scored
      </p>
      <dl className="mt-1.5 grid gap-x-8 gap-y-1 sm:grid-cols-2" style={{ color: INK.body }}>
        {entry.reportedWeightLb != null && (
          <div className="flex gap-2">
            <dt style={{ color: INK.muted }}>Weight, reported</dt>
            <dd className="font-medium">{entry.reportedWeightLb} lb</dd>
          </div>
        )}
        {outcomes.map(([key, value]) => (
          <div key={key} className="flex gap-2">
            <dt style={{ color: INK.muted }}>{OUTCOME_LABEL[key] ?? key}</dt>
            <dd className="font-medium">{outcomeText(key, value)}</dd>
          </div>
        ))}
      </dl>
      {entry.progressPhotoUrl && (
        <a href={entry.progressPhotoUrl} target="_blank" rel="noreferrer" className="mt-1.5 inline-block font-medium text-forest underline">
          Open the month {entry.month} progress photo
        </a>
      )}
    </div>
  );
}

function AnalysisMonth({ entry }: { entry: AnalysisEntry }) {
  const date = new Date(entry.assessmentAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const style = TIER_STYLE[entry.riskTier];
  return (
    <section className="border-t pt-4 first:border-t-0 first:pt-0" style={{ borderColor: INK.divider }}>
      <h3 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[15px]" style={{ fontWeight: 560, color: INK.heading }}>
        <span className="font-display text-[16px]">Month {entry.month}</span>
        <span className="font-sans text-[13.5px] font-normal" style={{ color: INK.muted }}>
          {date} · score {scoreText(entry.retainScore)} of 100
          {entry.trendDelta !== null && entry.trendDelta !== 0
            ? ` · ${entry.trendDelta > 0 ? 'up' : 'down'} ${pointsText(entry.trendDelta, 'points')}`
            : ''}
        </span>
        <span
          className="rounded-full px-2.5 py-[3px] font-sans text-[12px] font-semibold"
          style={{ backgroundColor: style.fill, color: style.text }}
        >
          {style.label}
        </span>
        <span className="font-mono text-[12px] font-normal" style={{ color: INK.faint }}>
          {entry.modelVersion} · {entry.questionVersion}
        </span>
      </h3>

      <dl className="mt-3 grid gap-x-8 gap-y-1 text-[13.5px] sm:grid-cols-2" style={{ color: INK.body }}>
        <div className="flex gap-2">
          <dt style={{ color: INK.muted }}>Primary driver</dt>
          <dd className="font-medium">{entry.primaryDriver ?? 'None'}</dd>
        </div>
        <div className="flex gap-2">
          <dt style={{ color: INK.muted }}>Secondary driver</dt>
          <dd className="font-medium">{entry.secondaryDriver ?? 'None'}</dd>
        </div>
        <div className="flex gap-2">
          <dt style={{ color: INK.muted }}>Action code</dt>
          <dd className="font-medium">{entry.recommendedAction}</dd>
        </div>
        <div className="flex gap-2">
          <dt style={{ color: INK.muted }}>Continuation trend</dt>
          <dd className="font-medium">{entry.continuationTrend ?? 'Needs two check-ins'}</dd>
        </div>
      </dl>

      <RecordedWithCheckIn entry={entry} />

      {entry.hardAlerts.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {entry.hardAlerts.map((alert) => (
            <li key={alert.ruleId} className="rounded-[8px] px-3 py-2 text-[13.5px]" style={{ backgroundColor: '#fdeaec' }}>
              <span className="font-mono text-[12px] font-semibold text-alert">{alert.ruleId}</span>{' '}
              <span className="font-semibold" style={{ color: '#9f1119' }}>
                {alert.alert}
              </span>
              <span style={{ color: INK.body }}>
                {' '}
                · {alert.because} Routed to {alert.routing}.
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-[13.5px]">
          <thead>
            <tr className="text-[11.5px] uppercase tracking-[0.06em]" style={{ color: INK.faint }}>
              <th className="pb-1.5 pr-4 font-semibold">Rule</th>
              <th className="pb-1.5 pr-4 font-semibold">Output</th>
              <th className="pb-1.5 pr-4 font-semibold">Because</th>
              <th className="pb-1.5 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {entry.rules.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-1.5" style={{ color: INK.muted }}>
                  No rule fired this month.
                </td>
              </tr>
            ) : (
              entry.rules.map((rule) => (
                <tr key={rule.ruleId} className="border-t align-top" style={{ borderColor: INK.divider }}>
                  <td className="py-1.5 pr-4 font-mono text-[12px] font-semibold" style={{ color: INK.heading }}>
                    {rule.ruleId}
                  </td>
                  <td className="py-1.5 pr-4 font-medium" style={{ color: INK.body }}>
                    {rule.output}
                  </td>
                  <td className="py-1.5 pr-4" style={{ color: INK.muted }}>
                    {rule.because}
                  </td>
                  <td className="py-1.5" style={{ color: INK.muted }}>
                    {rule.action}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.06em]" style={{ color: INK.faint }}>
            Domain scores, 0 to 100
          </p>
          <ul className="mt-1.5 space-y-1 text-[13.5px]">
            {DOMAIN_LABEL.map(([key, label]) => (
              <li key={key} className="flex justify-between gap-3 border-b pb-1" style={{ borderColor: INK.divider }}>
                <span style={{ color: INK.body }}>{label}</span>
                <span className="font-semibold tabular-nums" style={{ color: INK.heading }}>
                  {typeof entry.domains[key] === 'number' ? Math.round(entry.domains[key] as number) : 'Not scored'}
                </span>
              </li>
            ))}
          </ul>
          {entry.missingDependencies.length > 0 && (
            <p className="mt-2 text-[12.5px]" style={{ color: INK.muted }}>
              Rules not run because an answer was missing:{' '}
              <span className="font-mono text-[12px]">{entry.missingDependencies.join(', ')}</span>
            </p>
          )}
        </div>
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.06em]" style={{ color: INK.faint }}>
            What we asked, and what they answered
          </p>
          <ul className="mt-1.5 space-y-1 text-[13.5px]">
            {QUESTIONS.map(([ref, question]) => (
              <li key={ref} className="flex justify-between gap-3 border-b pb-1" style={{ borderColor: INK.divider }}>
                <span style={{ color: INK.body }}>{question}</span>
                <span className="shrink-0 text-right font-medium" style={{ color: INK.heading }}>
                  {answerText(entry.answers[ref])}
                </span>
              </li>
            ))}
            {typeof entry.answers.retain_patient_comment === 'string' && entry.answers.retain_patient_comment.trim() && (
              <li className="pt-1" style={{ color: INK.body }}>
                <span style={{ color: INK.muted }}>Comment, unscored: </span>“{entry.answers.retain_patient_comment}”
              </li>
            )}
          </ul>
        </div>
      </div>
    </section>
  );
}
